import {
  Col,
  Row,
  Space,
  notification,
  Divider,
  Dropdown,
  Menu,
  Pagination,
  Radio,
  Select,
  Spin,
  Tooltip,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/helpers';
import MobHeader from '../../Components/Header/MobHeader';
import {
  ThemeInput,
  ThemeMainWrapper,
  ThemePara1,
  ThemeTable,
  ThemeTableCard,
  CardPaginationWrapper,
  ThemeFilterBtn,
  ThemeButton,
  ThemeModal,
  ThemeTag,
  XThemeSelect,
} from '../Styles/Theme';
import { GreetingText } from '../Dashboard/DashboardStyle';
import {
  InsightEngineModalRadioGroup,
  InsightEngineModalTitle,
  ThemeProjectTabWrapper,
  ProjectListScrollable,
} from './ProjectsStyle';
import {
  SurveyCard,
  SurveyCardDate,
  SurveyCardDetails,
  SurveyCardHeader,
  SurveyCardInner,
  SurveyCardTitle,
  SurveyCardMenuIcon,
  SurveyNameLink,
  SurveyTableActionWrapper,
} from "../Surveys/SurveysStyle";
import { SearchOutlined, RightOutlined, SyncOutlined, LockOutlined, UsergroupAddOutlined, EllipsisOutlined } from "@ant-design/icons";
import useFolders from "../../hooks/useFolders";
import {
  FolderBackNav,
  FolderCardsSection,
  ItemsSectionLabel,
  NewFolderButton,
  FolderModals,
  FolderSelector,
} from "../../Components/FolderComponents";
import {
  getAllChatsApi,
  getAllChatsReset,
  deleteChatApi,
  deleteChatReset,
  createChatApi,
  createChatReset,
} from '../../store/actions/chatAction';
import {
  getProjectsApi,
  getProjectsReset,
} from '../../store/actions/projectAction';
import {
  useInsightsPermissions,
  useUserInfo,
  USER_TYPES,
} from '../../utils/permissions';
import {
  isProjectSharedToMe,
  hasSharePermission,
} from '../../utils/sharePermissions';

const CrowdPulseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginRight: 8, verticalAlign: 'middle' }}
  >
    <path
      d="M2 12h4l2-6 4 12 3-8 2 4h5"
      stroke="#6366f1"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CrowdLensIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginRight: 8, verticalAlign: 'middle' }}
  >
    <circle cx="12" cy="12" r="9" stroke="#10b981" strokeWidth="2" />
    <circle cx="12" cy="12" r="5" stroke="#10b981" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="2" fill="#10b981" />
  </svg>
);

const AI_PROVIDER_OPTIONS = {
  openai: {
    key: 'crowdpulse',
    providerKey: 'openai',
    name: 'Qualitative Analysis Engine',
    icon: CrowdPulseIcon,
    description: 'Where voices become vision',
  },
  gemini: {
    key: 'crowdlens',
    providerKey: 'gemini',
    name: 'Quantitative Analysis Engine',
    icon: CrowdLensIcon,
    description: 'See beyond the surface',
  },
};

const InsightEngine = props => {
  const {
    getAllChats,
    getAllChatsReset,
    getAllChatsState,
    deleteChat,
    deleteChatReset,
    deleteChatState,
    getProjects,
    getProjectsReset,
    getProjectsState,
    createChat,
    createChatReset,
    createChatState,
  } = props;

  const { can } = useInsightsPermissions();
  const { userId, userType } = useUserInfo();
  const navigate = useNavigate();

  // Check if user can create chats in a project
  // First check role-based permission, then check ownership or share permission
  const canCreateChatInProject = project => {
    // Role permission must be checked first - if role doesn't have 'create',
    // don't allow even if share permission exists
    if (!can('create')) return false;

    // Only Super Admin has full access to all projects
    if (userType === USER_TYPES.SUPER_ADMIN) return true;

    // Owner can create chats
    const isOwner =
      project?.created_by?._id === userId || project?.created_by === userId;
    if (isOwner) return true;

    // Shared user needs can_create_chats permission (includes admin if shared)
    if (isProjectSharedToMe(project)) {
      return hasSharePermission(project.sharePermissions, 'can_create_chats');
    }

    // Not owner and not shared - no permission to create chats
    return false;
  };

  const getProjectStatusColor = status => {
    const colorMap = {
      draft: 'default',
      indexing: 'processing',
      ready: 'success',
      deleting: 'error',
    };
    return colorMap[status] || 'default';
  };

  const getProjectStatusText = status => {
    const textMap = {
      draft: 'Draft',
      indexing: 'Indexing...',
      ready: 'Ready',
      deleting: 'Deleting...',
    };
    return textMap[status] || status;
  };

  const isProjectAvailable = project => {
    // Must be ready AND user must have permission to create chats
    return project.status === 'ready' && canCreateChatInProject(project);
  };

  const isProjectStatusReady = status => {
    return status === 'ready';
  };

  const getAvailableProjectsCount = () => {
    return projects.filter(
      p => p.status !== 'deleting' && isProjectAvailable(p)
    ).length;
  };

  const getUnavailableProjectsCount = () => {
    return projects.filter(
      p => p.status !== 'deleting' && !isProjectAvailable(p)
    ).length;
  };

  const [activeView, setActiveView] = useState('card');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [cardData, setCardData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectSearchValue, setProjectSearchValue] = useState('');
  const [sessionTitle, setSessionTitle] = useState('');
  const [aiProvider, setAiProvider] = useState('');
  const [isCreatingChat, setIsCreatingChat] = useState(false);

  const defaultFilters = {
    page: 1,
    limit: 12,
    search: '',
    sortBy: 'createdAt',
    sortDirection: 'desc',
  };

  const getFiltersFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilters = {};
    for (const [key, value] of urlParams.entries()) {
      urlFilters[key] = value;
    }
    const filters = {
      ...defaultFilters,
      ...urlFilters,
    };
    return filters;
  };

  const initialFilters = getFiltersFromUrl();
  const [filters, setFilters] = useState(initialFilters);
  const [searchValue, setSearchValue] = useState(initialFilters.search || '');

  // Shared folder hook
  const folderHook = useFolders({ entityType: 'chat', searchValue });
  const {
    folders,
    filteredFolders,
    currentFolderId,
    currentFolderName,
    refreshNeeded,
    createFolderModal,
    setCreateFolderModal,
    renameFolderModal,
    setRenameFolderModal,
    folderToRename,
    deleteFolderModal,
    setDeleteFolderModal,
    folderToDelete,
    moveToFolderModal,
    setMoveToFolderModal,
    selectedFolderId,
    setSelectedFolderId,
    createItemFolderId: createChatFolderId,
    setCreateItemFolderId: setCreateChatFolderId,
    removeFromFolderModal,
    setRemoveFromFolderModal,
    itemToRemove: chatToRemove,
    foldersCollapsed,
    setFoldersCollapsed,
    itemsCollapsed: chatsCollapsed,
    setItemsCollapsed: setChatsCollapsed,
    draggedItem: draggedChat,
    dragOverFolderId,
    folderForm,
    renameFolderForm,
    createFolderState,
    renameFolderState,
    deleteFolderState,
    addItemToFolderState,
    removeItemFromFolderState,
    fetchFolders,
    getFoldersState,
    handleGetFoldersResponse,
    handleCreateFolderResponse,
    handleRenameFolderResponse,
    handleDeleteFolderResponse,
    handleAddItemToFolderResponse,
    handleRemoveItemFromFolderResponse,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleMoveToFolder,
    handleRemoveFromFolder,
    confirmRemoveFromFolder,
    handleFolderClick,
    handleBackToRoot,
    openRenameFolderModal,
    openDeleteFolderModal,
    openMoveToFolderModal,
    handleDragStart,
    handleDragEnd,
    handleFolderDragOver,
    handleFolderDragEnter,
    handleFolderDragLeave,
    handleFolderDrop,
    addItemToFolder,
  } = folderHook;
  const createChatFolderIdRef = useRef(null);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Handle URL-based pagination and filters
  useEffect(() => {
    const updatedFilters = getFiltersFromUrl();
    setFilters({ ...updatedFilters });
    setSearchValue(updatedFilters.search || '');
    if (currentFolderId) {
      getAllChats({ ...updatedFilters, folderId: currentFolderId });
    } else {
      getAllChats({ ...updatedFilters, excludeFoldered: true });
    }
  }, [window.location.search, currentFolderId]);

  // Handle chat sessions response
  useEffect(() => {
    if (getAllChatsState.apiState === 'success' && getAllChatsState.data) {
      const chatsData = getAllChatsState.data.data || getAllChatsState.data;
      const chats = chatsData.chats || [];

      if (chats && chats.length > 0) {
        setTotalRecords(
          chatsData.pagination?.total || chatsData.total_count || 0
        );

        const updatedChats = chats.map(item => {
          const messageCount = item.messages?.length || 0;
          return {
            key: item._id,
            id: item._id,
            name: item.title || 'Untitled Chat',
            sessionName: item.title || 'Untitled Chat',
            messages: `${messageCount} ${messageCount === 1 ? 'Message' : 'Messages'}`,
            createdOn: formatDate(item.createdAt),
            projectName: item.project?.name || '-',
            sharedToMe: item.sharedToMe || false,
            chat: item,
          };
        });

        setTableData(updatedChats);
        setCardData(updatedChats);
      } else {
        setTableData([]);
        setCardData([]);
        setTotalRecords(0);
      }
      getAllChatsReset();
    } else if (getAllChatsState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description:
          getAllChatsState.message || 'Failed to fetch chat sessions',
      });
      getAllChatsReset();
    }
  }, [getAllChatsState, getAllChatsReset]);

  // Handle delete response
  useEffect(() => {
    if (deleteChatState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description:
          deleteChatState.message || 'Chat session deleted successfully',
      });
      setDeleteModalVisible(false);
      setSelectedChat(null);
      refreshChats();
    } else if (deleteChatState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: deleteChatState.message || 'Failed to delete chat session',
      });
    }
    deleteChatReset();
  }, [deleteChatState, deleteChatReset, getAllChats]);

  // Fetch projects when modal opens
  useEffect(() => {
    if (isModalOpen) {
      getProjects({ page: 1, limit: 100, includeInferenceOnly: true });
    }
    return () => {
      getProjectsReset();
    };
  }, [isModalOpen, getProjects, getProjectsReset]);

  // Handle projects response
  useEffect(() => {
    if (getProjectsState.apiState === 'success' && getProjectsState.data) {
      const projectsData = getProjectsState.data.data || getProjectsState.data;
      setProjects(projectsData.projects || []);
      getProjectsReset();
    } else if (getProjectsState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getProjectsState.message || 'Failed to fetch projects',
      });
      getProjectsReset();
    }
  }, [getProjectsState, getProjectsReset]);

  // Handle create chat response
  useEffect(() => {
    if (createChatState.apiState === 'success') {
      const chatId = createChatState.data?.data?._id;
      const folderId = createChatFolderIdRef.current;
      if (folderId && chatId) {
        addItemToFolder(folderId, chatId);
      }
      setIsCreatingChat(false);
      setIsModalOpen(false);
      setSessionTitle('');
      setSelectedProjectId(null);
      setSelectedProject(null);
      setAiProvider('');
      setProjectSearchValue('');
      setCreateChatFolderId(null);
      createChatFolderIdRef.current = null;
      createChatReset();
      refreshChats();
      if (chatId) {
        navigate(`/insight-engine/${chatId}`);
      }
    } else if (createChatState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: createChatState.message || 'Failed to create chat session',
      });
      setIsCreatingChat(false);
      createChatReset();
    }
  }, [createChatState, createChatReset, navigate, getAllChats]);

  // Helper to re-fetch chats with current folder context
  const refreshChats = () => {
    const updatedFilters = getFiltersFromUrl();
    if (currentFolderId) {
      getAllChats({ ...updatedFilters, folderId: currentFolderId });
    } else {
      getAllChats({ ...updatedFilters, excludeFoldered: true });
    }
  };

  // Folder hook: fetch on mount
  useEffect(() => {
    fetchFolders();
  }, []);

  // Folder hook: response handlers
  useEffect(() => { handleGetFoldersResponse(); }, [getFoldersState]);
  useEffect(() => { handleCreateFolderResponse(); }, [createFolderState]);
  useEffect(() => { handleRenameFolderResponse(); }, [renameFolderState]);
  useEffect(() => { handleDeleteFolderResponse(); }, [deleteFolderState]);
  useEffect(() => { handleAddItemToFolderResponse(); }, [addItemToFolderState]);
  useEffect(() => { handleRemoveItemFromFolderResponse(); }, [removeItemFromFolderState]);

  // Re-fetch chats when folder hook signals a refresh (after folder operations)
  useEffect(() => {
    if (refreshNeeded > 0) {
      refreshChats();
    }
  }, [refreshNeeded]);

  // Build chat detail URL with folder context for back navigation
  const getChatDetailUrl = (chatId) => {
    if (currentFolderId) {
      return `/insight-engine/${chatId}?fromFolderId=${currentFolderId}&fromFolderName=${encodeURIComponent(currentFolderName)}`;
    }
    return `/insight-engine/${chatId}`;
  };

  const handleOpenCreateModal = () => {
    setIsModalOpen(true);
    // Auto-select current folder when inside a folder
    if (currentFolderId) {
      setCreateChatFolderId(currentFolderId);
    }
  };

  const handleSearch = value => {
    const queryParams = new URLSearchParams(window.location.search);
    if (value) {
      queryParams.set('search', value);
    } else {
      queryParams.delete('search');
    }
    queryParams.set('page', '1');
    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    setFilters({ ...filters, search: value, page: 1 });
    setSearchValue(value);
  };

  const handleTableChange = (pagination, _, sorter) => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('page', pagination.current || 1);
    queryParams.set('limit', pagination.pageSize || 12);

    if (sorter && Object.keys(sorter).length > 0) {
      queryParams.set('sortBy', sorter.field || 'createdAt');
      queryParams.set(
        'sortDirection',
        sorter.order === 'ascend' ? 'asc' : 'desc'
      );
    } else {
      queryParams.set('sortBy', 'createdAt');
      queryParams.set('sortDirection', 'desc');
    }

    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    setFilters({
      ...filters,
      sortBy: sorter?.field || 'createdAt',
      sortDirection: sorter?.order === 'ascend' ? 'asc' : 'desc',
      page: pagination.current || 1,
      limit: pagination.pageSize || 12,
    });
  };

  const handleDelete = record => {
    setSelectedChat(record);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    if (selectedChat) {
      deleteChat(selectedChat.id);
    }
  };

  const handleCardClick = chatId => {
    navigate(getChatDetailUrl(chatId));
  };

  const handleProjectSelect = projectId => {
    setSelectedProjectId(projectId);
    const project = projects.find(p => p._id === projectId);
    setSelectedProject(project);
    setAiProvider(''); // Reset AI provider when project changes
  };

  const handleCreateSession = async () => {
    if (!sessionTitle.trim()) {
      notification.warning({
        message: 'Session Name Required',
        description: 'Please enter a name for your session',
      });
      return;
    }
    if (!selectedProjectId) {
      notification.warning({
        message: 'Project Required',
        description: 'Please select a project',
      });
      return;
    }
    setIsCreatingChat(true);
    createChatFolderIdRef.current = createChatFolderId;
    try {
      await createChat({
        projectId: selectedProjectId,
        title: sessionTitle.trim(),
        aiProvider: aiProvider || undefined,
      });
    } catch (error) {
      setIsCreatingChat(false);
    }
  };

  const handleModalCancel = () => {
    if (!isCreatingChat) {
      setIsModalOpen(false);
      setSessionTitle('');
      setSelectedProjectId(null);
      setSelectedProject(null);
      setAiProvider('');
      setProjectSearchValue('');
      setCreateChatFolderId(null);
    }
  };

  const renderCardMenu = record => {
    return (
      <Menu
        selectable={false}
        onClick={({ key, domEvent }) => {
          domEvent.stopPropagation();
          if (key === 'view') {
            handleCardClick(record.id);
          } else if (key === "moveToFolder") {
            openMoveToFolderModal(record);
          } else if (key === "removeFromFolder") {
            handleRemoveFromFolder(record);
          } else if (key === 'delete') {
            handleDelete(record);
          }
        }}
      >
        <Menu.Item key="view">View Details</Menu.Item>
        <Menu.Item key="moveToFolder">Move to Folder</Menu.Item>
        {currentFolderId && (
          <Menu.Item key="removeFromFolder">Remove from Folder</Menu.Item>
        )}
        {can('delete') && (
          <>
            <Menu.Divider />
            <Menu.Item key="delete">Delete</Menu.Item>
          </>
        )}
      </Menu>
    );
  };

  const columns = [
    {
      title: 'Session Name',
      dataIndex: 'sessionName',
      key: 'sessionName',
      render: (text, record) => (
        <SurveyNameLink onClick={() => handleCardClick(record.id)}>
          {text}
        </SurveyNameLink>
      ),
    },
    {
      title: 'Messages',
      dataIndex: 'messages',
      key: 'messages',
      render: text => <span>{text || '-'}</span>,
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      key: 'createdOn',
      width: 180,
      sorter: true,
      render: (text, record) => (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {record.sharedToMe && (
            <>
              <Tooltip title="Shared with you">
                <UsergroupAddOutlined
                  style={{
                    fontSize: 14,
                    color: '#8b5cf6',
                    marginRight: 8,
                  }}
                />
              </Tooltip>
              <span style={{ marginRight: 8, color: '#d9d9d9' }}>|</span>
            </>
          )}
          {text || '-'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_, record) => (
        <SurveyTableActionWrapper>
          <ThemeFilterBtn
            shape="circle"
            size="medium"
            icon={<RightOutlined />}
            onClick={() => handleCardClick(record.id)}
          />
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            dropdownRender={() => renderCardMenu(record)}
          >
            <ThemeFilterBtn
              shape="circle"
              size="medium"
              icon={<EllipsisOutlined style={{ fontSize: 16 }} />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </SurveyTableActionWrapper>
      ),
    },
  ];

  return (
    <>
      <MobHeader />
      <ThemeMainWrapper>
        <Row gutter={[16, 16]} className="mb-6" align="middle">
          <Col
            xs={{ span: 20, order: 1 }}
            sm={{ span: 8, order: 1 }}
            md={{ span: 6, order: 1 }}
            lg={6}
            xl={8}
          >
            <GreetingText>Analyze</GreetingText>
          </Col>
          <Col
            xs={{ span: 24, order: 3 }}
            sm={{ span: 24, order: 3 }}
            md={{ span: 8, order: 1 }}
            lg={8}
            xl={8}
            className="text-center sm:text-center md:text-end lg:text-center xl:text-center"
          >
            <ThemeProjectTabWrapper>
              <div className="view-toggle">
                <button
                  className={`toggle-btn ${
                    activeView === 'card' ? 'active' : ''
                  }`}
                  onClick={() => setActiveView('card')}
                >
                  Card View
                </button>
                <button
                  className={`toggle-btn ${
                    activeView === 'table' ? 'active' : ''
                  }`}
                  onClick={() => setActiveView('table')}
                >
                  Table View
                </button>
              </div>
            </ThemeProjectTabWrapper>
          </Col>
          <Col
            xs={{ span: 4, order: 2 }}
            sm={{ span: 16, order: 2 }}
            md={{ span: 10, order: 1 }}
            lg={10}
            xl={8}
            className="text-end"
          >
            <Space gap={10}>
              {can('create') && (
                isMobile ? (
                  <>
                    {!currentFolderId && (
                      <ThemeFilterBtn
                        className="background"
                        size="large"
                        shape="square"
                        icon={<img src="/images/folder-open-purple.svg" alt="Folder" style={{ width: 18, height: 18 }} />}
                        onClick={() => setCreateFolderModal(true)}
                      />
                    )}
                    <ThemeFilterBtn
                      className="background"
                      size="large"
                      shape="square"
                      icon={<img src="/images/plus-icon.svg" alt="New Session" />}
                      onClick={handleOpenCreateModal}
                    />
                  </>
                ) : (
                  <>
                    {!currentFolderId && (
                      <NewFolderButton isMobile={false} onClick={() => setCreateFolderModal(true)} />
                    )}
                    <ThemeButton
                      size="large"
                      icon={<img src="/images/plus-icon.svg" alt="New Session" />}
                      onClick={handleOpenCreateModal}
                    >
                      New Session
                    </ThemeButton>
                  </>
                )
              )}
            </Space>
          </Col>
        </Row>
        {currentFolderId && (
          <FolderBackNav currentFolderName={currentFolderName} onBack={handleBackToRoot} />
        )}
        <Row gutter={[16, 16]} className="mb-6" align="middle">
          <Col xs={24} sm={12} md={12} lg={10} xl={6} xxl={4}>
            <ThemeInput
              className="shadow"
              placeholder="Search insights engine sessions..."
              size="large"
              shape="square"
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={e => {
                const value = e.target.value;
                setSearchValue(value);
                if (!value) {
                  handleSearch('');
                }
              }}
              onPressEnter={e => handleSearch(e.target.value)}
            />
          </Col>
          {/* <Col xs={24} sm={12} md={14} lg={16} xl={16} className="text-end">
              <ThemeFilterBtn
                size="large"
                shape="square"
                icon={<img src="/images/filter-icon.svg" alt="Filter" />}
              >
                Filter
              </ThemeFilterBtn>
            </Col> */}
        </Row>
        {activeView === 'card' && (
          <>
            {/* Folder section (only at root, not inside a folder) */}
            {!currentFolderId && (
              <>
                <FolderCardsSection
                  filteredFolders={filteredFolders}
                  foldersCollapsed={foldersCollapsed}
                  setFoldersCollapsed={setFoldersCollapsed}
                  onFolderClick={handleFolderClick}
                  onRenameFolderClick={openRenameFolderModal}
                  onDeleteFolderClick={openDeleteFolderModal}
                  enableDrag={true}
                  dragOverFolderId={dragOverFolderId}
                  onDragOver={handleFolderDragOver}
                  onDragEnter={handleFolderDragEnter}
                  onDragLeave={handleFolderDragLeave}
                  onDrop={handleFolderDrop}
                />
                {filteredFolders.length > 0 && cardData.length > 0 && (
                  <ItemsSectionLabel label="Sessions" itemsCollapsed={chatsCollapsed} setItemsCollapsed={setChatsCollapsed} />
                )}
              </>
            )}
            {!(chatsCollapsed && filteredFolders.length > 0 && !currentFolderId) && (
            <>
            <Row gutter={[16, 16]} className="mb-6">
              {getAllChatsState.apiState === 'loading' ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <Col
                    span={24}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={8}
                    xxl={6}
                    xxxl={4}
                    key={`skeleton-${index}`}
                  >
                    <SurveyCard loading={true} />
                  </Col>
                ))
              ) : cardData.length > 0 ? (
                cardData.map(item => (
                  <Col
                   
                    span={24}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={8}
                    xxl={6}
                    xxxl={4}
                    key={item.key}
                  
                    draggable={!currentFolderId && filteredFolders.length > 0}
                    onDragStart={!currentFolderId && filteredFolders.length > 0 ? (e) => handleDragStart(e, item) : undefined}
                    onDragEnd={!currentFolderId && filteredFolders.length > 0 ? handleDragEnd : undefined}
                    style={!currentFolderId && filteredFolders.length > 0 ? { cursor: 'grab' } : undefined}
                  >
                    <SurveyCard onClick={() => handleCardClick(item.id)}>
                      <SurveyCardInner>
                        <SurveyCardHeader>
                          <span className="title">{item.messages}</span>
                          <Dropdown
                            trigger={['click']}
                            placement="bottomRight"
                            dropdownRender={() => renderCardMenu(item)}
                          >
                            <SurveyCardMenuIcon
                              onClick={e => e.stopPropagation()}
                            >
                              <img
                                src="/images/menu-dots-vertical.svg"
                                alt="Menu"
                              />
                            </SurveyCardMenuIcon>
                          </Dropdown>
                        </SurveyCardHeader>
                        <SurveyCardDetails className="my-1">
                          <SurveyCardTitle>{item.sessionName}</SurveyCardTitle>
                          {item.projectName && item.projectName !== '-' && (
                            <div className="text-sm text-gray-500 mt-1">
                              {item.projectName}
                            </div>
                          )}
                        </SurveyCardDetails>
                        <Space className="justify-between">
                          <SurveyCardDate>
                            {item.sharedToMe && (
                              <>
                                <Tooltip title="Shared with you">
                                  <UsergroupAddOutlined
                                    style={{
                                      fontSize: 14,
                                      color: '#8b5cf6',
                                      marginRight: 8,
                                    }}
                                  />
                                </Tooltip>
                                <span
                                  style={{ marginRight: 8, color: '#d9d9d9' }}
                                >
                                  |
                                </span>
                              </>
                            )}
                            <img
                              src="/images/calendar-icon.svg"
                              alt="Calendar"
                            />
                            <span>{item.createdOn}</span>
                          </SurveyCardDate>
                          <ThemeFilterBtn
                            className="btnBorder"
                            shape="circle"
                            size="medium"
                            icon={<img src="/images/arrow-right.svg" alt="#" />}
                          />
                        </Space>
                      </SurveyCardInner>
                    </SurveyCard>
                  </Col>
                ))
              ) : (
                <Col span={24} className="text-center">
                  <p>{currentFolderId ? 'No sessions in this folder' : 'No chat sessions found'}</p>
                </Col>
              )}
            </Row>
            {totalRecords > 0 && (
              <CardPaginationWrapper>
                <Pagination
                  current={parseInt(filters.page) || 1}
                  pageSize={parseInt(filters.limit) || 12}
                  total={totalRecords}
                  showSizeChanger={true}
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} chat sessions`
                  }
                  onChange={(page, pageSize) =>
                    handleTableChange({ current: page, pageSize }, null, {})
                  }
                />
              </CardPaginationWrapper>
            )}
            </>
            )}
          </>
        )}
        {activeView === 'table' && (
          <>
            {/* Folder navigation for table view */}
            {!currentFolderId && (
              <>
                <FolderCardsSection
                  filteredFolders={filteredFolders}
                  foldersCollapsed={foldersCollapsed}
                  setFoldersCollapsed={setFoldersCollapsed}
                  onFolderClick={handleFolderClick}
                  onRenameFolderClick={openRenameFolderModal}
                  onDeleteFolderClick={openDeleteFolderModal}
                />
                {filteredFolders.length > 0 && (
                  <ItemsSectionLabel label="Sessions" itemsCollapsed={chatsCollapsed} setItemsCollapsed={setChatsCollapsed} />
                )}
              </>
            )}
            {!(chatsCollapsed && filteredFolders.length > 0 && !currentFolderId) && (
            <ThemeTableCard>
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <ThemeTable
                    columns={columns}
                    dataSource={tableData}
                    pagination={{
                      current: parseInt(filters.page) || 1,
                      pageSize: parseInt(filters.limit) || 12,
                      total: totalRecords,
                      showSizeChanger: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} chat sessions`,
                    }}
                    onChange={handleTableChange}
                    loading={getAllChatsState.apiState === 'loading'}
                    scroll={{ x: 768 }}
                  />
                </Col>
              </Row>
            </ThemeTableCard>
            )}
          </>
        )}
      </ThemeMainWrapper>

      <ThemeModal
        title={<img src="/images/modal-title-icon.svg" alt="Analyze" />}
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
        centered={true}
        width={600}
      >
        <InsightEngineModalTitle className="mb-3">
          Start New Session
        </InsightEngineModalTitle>

        {/* Session Title */}
        <div className="mb-3">
          <label className="block text-sm font-medium mb-2">
            What would you like to call this session?
          </label>
          <ThemeInput
            size="large"
            placeholder="e.g., Youth Sentiment Analysis, Q4 Insights..."
            value={sessionTitle}
            onChange={e => setSessionTitle(e.target.value)}
            onPressEnter={() => {
              if (sessionTitle.trim() && selectedProjectId && !isCreatingChat) {
                handleCreateSession();
              }
            }}
            disabled={isCreatingChat}
          />
        </div>

        {/* Project Selection */}
        <InsightEngineModalTitle className="small mb-1">
          <span>*</span>Select Project
        </InsightEngineModalTitle>
        <ThemePara1 className="font14 mb-2">
          Choose which project you want to chat with. Select one project to
          analyze its data.
          {projects.length > 0 && (
            <span
              style={{
                display: 'block',
                marginTop: 4,
                fontSize: 12,
                color: '#666',
              }}
            >
              {getAvailableProjectsCount()} available
              {getUnavailableProjectsCount() > 0 &&
                ` (${getUnavailableProjectsCount()} not ready)`}
            </span>
          )}
        </ThemePara1>

        {getProjectsState.apiState === 'loading' ? (
          <div className="text-center py-4">
            <Spin />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <p>No projects available</p>
          </div>
        ) : (
          <>
          {projects.filter(p => p.status !== 'deleting').length > 5 && (
            <div style={{ marginBottom: 8 }}>
              <ThemeInput
                placeholder="Search projects..."
                size="large"
                prefix={<SearchOutlined />}
                value={projectSearchValue}
                onChange={(e) => setProjectSearchValue(e.target.value)}
                allowClear
              />
            </div>
          )}
          {(() => {
            const filtered = projects.filter(p => p.status !== 'deleting').filter(p =>
              !projectSearchValue || p.name?.toLowerCase().includes(projectSearchValue.toLowerCase())
            );
            return filtered.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#666', background: '#f9f9f9', borderRadius: 8 }}>
                No projects match your search
              </div>
            ) : (
          <ProjectListScrollable>
            <InsightEngineModalRadioGroup
              className="mt-2"
              onChange={e => handleProjectSelect(e.target.value)}
              value={selectedProjectId}
            >
              {filtered.map((project) => {
                const isAvailable = isProjectAvailable(project);
                const isReady = isProjectStatusReady(project.status);
                const hasCreatePermission = canCreateChatInProject(project);

                  // Determine tooltip message
                  let tooltipMessage = '';
                  if (!isReady) {
                    if (project.status === 'indexing') {
                      tooltipMessage =
                        'Project is being processed. Please wait.';
                    } else {
                      tooltipMessage =
                        'This is a draft project. Upload files to make it ready.';
                    }
                  } else if (!hasCreatePermission) {
                    tooltipMessage =
                      'You do not have permission to create insight sessions in this project.';
                  }

                  const descText =
                    project.description && project.description !== '-'
                      ? project.description
                      : '';
                  const isDescLong = descText.length > 80;

                  const radioContent = (
                    <Radio
                      key={project._id}
                      value={project._id}
                      disabled={!isAvailable}
                    >
                      <div className="radio-label-content">
                        <div className="radio-title">{project.name}</div>
                        {descText ? (
                          <Tooltip
                            title={isDescLong ? descText : null}
                            placement="top"
                            overlayStyle={{ maxWidth: 350 }}
                          >
                            <div
                              className="radio-subtitle"
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: '#9ca3af',
                              }}
                            >
                              {descText}
                            </div>
                          </Tooltip>
                        ) : (
                          <div
                            className="radio-subtitle"
                            style={{ color: '#9ca3af' }}
                          >
                            No description
                          </div>
                        )}
                        <div className="radio-status">
                          <ThemeTag
                            color={getProjectStatusColor(project.status)}
                            className="status-tag-small"
                          >
                            {project.status === 'indexing' && (
                              <SyncOutlined
                                spin
                                style={{ marginRight: '4px', fontSize: '10px' }}
                              />
                            )}
                            {getProjectStatusText(project.status)}
                          </ThemeTag>
                        </div>
                        {!isAvailable && project.status === 'indexing' && (
                          <div className="disabled-indicator">
                            <LockOutlined />
                            <span>Processing...</span>
                          </div>
                        )}
                        {isReady && !hasCreatePermission && (
                          <div className="disabled-indicator">
                            <LockOutlined />
                            <span>No permission</span>
                          </div>
                        )}
                      </div>
                      <div className="radio-icon">
                        <img src="/images/check-icon.svg" alt="Selected" />
                      </div>
                    </Radio>
                  );

                  return isAvailable ? (
                    <React.Fragment key={project._id}>
                      {radioContent}
                    </React.Fragment>
                  ) : (
                    <Tooltip
                      key={project._id}
                      title={tooltipMessage}
                      placement="top"
                    >
                      <div>{radioContent}</div>
                    </Tooltip>
                  );
                })}
            </InsightEngineModalRadioGroup>
          </ProjectListScrollable>
            );
          })()}
          </>
        )}

        {/* AI Provider Selection - always visible but optional */}
        <Divider style={{ margin: '16px 0' }} />
        <div>
          <label className="d-flex text-sm font-medium mb-2">
            Select Analysis Engine{' '}
            <span style={{ color: '#999', fontWeight: 'normal' }}>
              (Optional)
            </span>
          </label>
          <Select
            size="large"
            style={{ width: '100%' }}
            value={aiProvider || undefined}
            onChange={value => setAiProvider(value)}
            disabled={isCreatingChat}
            placeholder={
              selectedProject
                ? 'Use project default'
                : 'Select a project first to see available engines'
            }
            allowClear
            onClear={() => setAiProvider('')}
          >
            {Object.values(AI_PROVIDER_OPTIONS)
              .filter(opt => {
                // If no project selected, show no options (placeholder will guide user)
                if (!selectedProject?.vector_stores) {
                  return false;
                }
                if (opt.providerKey === 'openai') {
                  return (
                    selectedProject.vector_stores?.openai?.status === 'active'
                  );
                }
                if (opt.providerKey === 'gemini') {
                  return (
                    selectedProject.vector_stores?.gemini?.status === 'active'
                  );
                }
                return false;
              })
              .map(opt => {
                const IconComponent = opt.icon;
                return (
                  <Select.Option key={opt.key} value={opt.key}>
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <IconComponent />
                      {opt.name}
                    </span>
                  </Select.Option>
                );
              })}
          </Select>
        </div>

        {/* Folder Selection - Optional */}
        <FolderSelector
          folders={folders}
          currentFolderId={currentFolderId}
          currentFolderName={currentFolderName}
          createItemFolderId={createChatFolderId}
          setCreateItemFolderId={setCreateChatFolderId}
          XThemeSelect={XThemeSelect}
        />

        {/* Footer Buttons */}
        <div className='text-end mt-3'>
          <ThemeButton
            size='large'
            className='noBackground mr-3'
            onClick={handleModalCancel}
            disabled={isCreatingChat}
          >
            Cancel
          </ThemeButton>
          <ThemeButton
            size='large'
            onClick={handleCreateSession}
            loading={isCreatingChat}
            disabled={
              isCreatingChat || !sessionTitle.trim() || !selectedProjectId
            }
          >
            {isCreatingChat ? 'Creating...' : 'Start Session'}
          </ThemeButton>
        </div>
      </ThemeModal>

      {/* Delete Modal */}
      <ThemeModal
        title="Delete Chat Session"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedChat(null);
        }}
        confirmLoading={deleteChatState.apiState === 'loading'}
        okText="Delete"
        cancelText="Cancel"
        centered
      >
        <p>
          {`Are you sure you want to delete chat session "${selectedChat?.sessionName}"?`}
        </p>
        <p>This action cannot be undone.</p>
      </ThemeModal>

      {/* Folder Modals (Create, Rename, Delete, Move, Remove) */}
      <FolderModals
        createFolderModal={createFolderModal}
        setCreateFolderModal={setCreateFolderModal}
        folderForm={folderForm}
        handleCreateFolder={handleCreateFolder}
        createFolderState={createFolderState}
        renameFolderModal={renameFolderModal}
        setRenameFolderModal={setRenameFolderModal}
        folderToRename={folderToRename}
        renameFolderForm={renameFolderForm}
        handleRenameFolder={handleRenameFolder}
        renameFolderState={renameFolderState}
        deleteFolderModal={deleteFolderModal}
        setDeleteFolderModal={setDeleteFolderModal}
        folderToDelete={folderToDelete}
        handleDeleteFolder={handleDeleteFolder}
        deleteFolderState={deleteFolderState}
        removeFromFolderModal={removeFromFolderModal}
        setRemoveFromFolderModal={setRemoveFromFolderModal}
        itemToRemove={chatToRemove}
        confirmRemoveFromFolder={confirmRemoveFromFolder}
        removeItemFromFolderState={removeItemFromFolderState}
        moveToFolderModal={moveToFolderModal}
        setMoveToFolderModal={setMoveToFolderModal}
        itemToMove={folderHook.itemToMove}
        selectedFolderId={selectedFolderId}
        setSelectedFolderId={setSelectedFolderId}
        handleMoveToFolder={handleMoveToFolder}
        addItemToFolderState={addItemToFolderState}
        folders={folders}
        currentFolderId={currentFolderId}
        itemLabel="session"
      />
    </>
  );
};

const mapStateToProps = state => ({
  getAllChatsState: state.chat.getAllChatsState,
  deleteChatState: state.chat.deleteChatState,
  getProjectsState: state.project.getProjectsState,
  createChatState: state.chat.createChatState,
});

const mapDispatchToProps = dispatch => ({
  getAllChats: params => dispatch(getAllChatsApi(params)),
  getAllChatsReset: () => dispatch(getAllChatsReset()),
  deleteChat: id => dispatch(deleteChatApi(id)),
  deleteChatReset: () => dispatch(deleteChatReset()),
  getProjects: params => dispatch(getProjectsApi(params)),
  getProjectsReset: () => dispatch(getProjectsReset()),
  createChat: data => dispatch(createChatApi(data)),
  createChatReset: () => dispatch(createChatReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(InsightEngine);
